import { z } from 'zod';
import { generateStructuredObject } from './structured.js';

/**
 * Prompt tools (decompose, facet, diversify, wizard combinations). Ported from
 * the retired server REST routes; each resolves to the JSON body those routes
 * returned, so existing callers are unchanged.
 */

// Schema for a single component
const componentSchema = z.object({
  id: z.string().describe('Unique identifier for this component'),
  type: z.string().describe('Type of component: subject, action, style, mood, setting, colors, lighting, details, composition'),
  label: z.string().describe('Human-readable label for this component'),
  value: z.string().describe('The actual content/value of this component extracted from the prompt')
});

// Schema for the decomposition response
const decompositionSchema = z.object({
  components: z.array(componentSchema).describe('Array of semantic components extracted from the prompt'),
  connections: z.array(z.object({
    source: z.string().describe('ID of the source component'),
    target: z.string().describe('ID of the target component')
  })).describe('Connections between components showing relationships')
});

export async function decomposePrompt(prompt) {
  if (!prompt) {
    throw new Error('No prompt provided');
  }

  const systemPrompt = `You are an expert at analyzing creative prompts and breaking them down into semantic components.
Analyze the given image generation prompt and extract key components into these categories:

- subject: The main subject/focus of the image (person, object, creature, etc.)
- action: What the subject is doing (if applicable)
- style: Artistic style, medium, technique (e.g., "oil painting", "cinematic", "anime")
- mood: Emotional tone or atmosphere (e.g., "mysterious", "joyful", "melancholic")
- setting: Location, environment, background
- colors: Dominant color palette or specific colors mentioned
- lighting: Time of day, light source, lighting quality
- details: Textures, patterns, specific features, accessories
- composition: Framing, perspective, layout elements

Rules:
1. Create unique IDs using the format "{type}-1" (e.g., "subject-1", "style-1")
2. IMPORTANT: Always include ALL 9 component types listed above, even if they are not present in the prompt
3. For components not found in the prompt, set value to an empty string ""
4. Don't invent details not mentioned in the prompt - leave value empty if not present
5. Keep values concise but complete when extracting from the prompt
6. Create connections that show logical relationships (e.g., subject connects to action, mood affects lighting)
7. Only create connections between components that have non-empty values`;

  const userPrompt = `Decompose this image generation prompt into semantic components:

"${prompt}"

Extract each meaningful component with its type, label, and value. Create logical connections between related components.`;

  const object = await generateStructuredObject({
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ],
    schema: decompositionSchema,
    temperature: 0.5
  });

  // Add position data for the flow diagram (left-to-right layout):
  // primary row (subject, action, style) and secondary row (rest)
  let primaryX = 0;
  let secondaryX = 0;

  object.components.forEach((component) => {
    const type = component.type.toLowerCase();
    const isPrimary = ['subject', 'action', 'style'].includes(type);

    if (isPrimary) {
      component.position = { x: primaryX * 300, y: 50 };
      primaryX++;
    } else {
      component.position = { x: secondaryX * 250, y: 200 };
      secondaryX++;
    }

    // Add isEmpty flag for placeholder styling
    component.isEmpty = component.value.trim() === '';

    // Add default facet state
    component.isFaceted = false;
    component.facetId = null;
  });

  return {
    success: true,
    components: object.components,
    connections: object.connections,
    timestamp: new Date().toISOString()
  };
}

// Schema for facet variations
const facetSchema = z.object({
  variants: z.array(z.string()).length(4).describe('4 creative variations of the component based on the directive')
});

export async function facetComponent(componentType, currentValue, directive) {
  if (!componentType || !currentValue) {
    throw new Error('Component type and current value are required');
  }

  if (!directive) {
    throw new Error('A directive is required to generate variations');
  }

  const systemPrompt = `You are an expert at generating creative variations for image generation prompts.
Given a component type, its current value, and a user's directive, generate 4 distinct variations.

Rules:
1. Each variation should be different but related to the original
2. Follow the user's directive/instruction for the direction of variations
3. Keep variations concise but descriptive (similar length to the original)
4. Ensure variations are appropriate for image generation prompts
5. Make each variation meaningfully different from the others
6. Maintain the same component type (e.g., if it's a style, all variations should be styles)

Component Types:
- subject: Main focus of the image
- action: What the subject is doing
- style: Artistic style, medium, technique
- mood: Emotional tone, atmosphere
- setting: Location, environment
- colors: Color palette
- lighting: Light source, quality, time of day
- details: Textures, patterns, specific features
- composition: Framing, perspective`;

  const userPrompt = `Component Type: ${componentType}
Current Value: "${currentValue}"
User Directive: "${directive}"

Generate 4 creative variations of this component based on the directive. Each should be distinct but follow the creative direction specified.`;

  const object = await generateStructuredObject({
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ],
    schema: facetSchema,
    temperature: 0.9
  });

  return {
    success: true,
    variants: object.variants,
    timestamp: new Date().toISOString()
  };
}

function requireNonEmptyString(value, name) {
  if (!value || typeof value !== 'string' || !value.trim()) {
    throw new Error(`${name} is required and must be a non-empty string`);
  }
}

/**
 * Apply the user's diversification instruction to one master prompt.
 */
export async function diversifyPrompt(masterPrompt, diversifyInstruction) {
  requireNonEmptyString(masterPrompt, 'masterPrompt');
  requireNonEmptyString(diversifyInstruction, 'diversifyInstruction');

  const schema = z.object({
    updatedPrompt: z.string().describe('The master prompt with the requested diversification applied'),
    notes: z.string().nullish().describe('Brief notes explaining what changed')
  });

  const systemPrompt = `You are an expert at refining and modifying AI image generation prompts for the Flux.1 model.

Your task is to take a master prompt and apply specific diversification changes requested by the user.

Key requirements:
- Apply ONLY the changes requested by the user
- Maintain the core concept and essence of the original prompt
- Keep the prompt concise and focused (100-200 words)
- Flux does NOT support negative prompting - use only positive descriptions
- Focus on visual elements: style, mood, lighting, composition, colors
- CRITICAL: Write prompts as direct visual descriptions. NEVER start with "Create an image", "Generate", "Imagine", or similar instruction phrases. Just describe what's in the image directly.
- Be precise and specific in applying the user's requested changes`;

  const userPrompt = `Original Master Prompt:
"${masterPrompt}"

User's Diversification Request:
"${diversifyInstruction}"

Please update the master prompt by applying the user's requested changes while maintaining the core concept. Return the updated prompt.`;

  const result = await generateStructuredObject({
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ],
    schema,
    temperature: 0.7, // Slightly lower temperature for more controlled modifications
    maxTokens: 1500
  });

  return {
    success: true,
    updatedPrompt: result.updatedPrompt,
    notes: result.notes || null,
    timestamp: new Date().toISOString()
  };
}

/**
 * Apply the same diversification instruction to every variant prompt.
 */
export async function diversifyPrompts(prompts, diversifyInstruction) {
  if (!prompts || !Array.isArray(prompts) || prompts.length === 0) {
    throw new Error('prompts is required and must be a non-empty array');
  }
  requireNonEmptyString(diversifyInstruction, 'diversifyInstruction');

  const schema = z.object({
    prompts: z.array(z.string()).describe('The diversified prompts in the same order as input'),
    notes: z.string().nullish().describe('Brief notes explaining what changed')
  });

  const systemPrompt = `You are an expert at refining and modifying AI image generation prompts for the Flux.1 model.

Your task is to take multiple variant prompts and apply the SAME diversification changes to ALL of them while maintaining their individual unique characteristics.

Key requirements:
- Apply the SAME type of changes to ALL prompts consistently
- Maintain each prompt's unique elements and distinguishing features
- Keep prompts concise and focused (100-200 words each)
- Flux does NOT support negative prompting - use only positive descriptions
- Focus on visual elements: style, mood, lighting, composition, colors
- CRITICAL: Write prompts as direct visual descriptions. NEVER start with "Create an image", "Generate", "Imagine", or similar instruction phrases. Just describe what's in the image directly.
- Be precise and specific in applying the user's requested changes
- Return EXACTLY the same number of prompts as provided, in the same order`;

  const promptsList = prompts.map((p, i) => `Variant ${i + 1}:\n"${p}"`).join('\n\n');

  const userPrompt = `Original Variant Prompts:
${promptsList}

User's Diversification Request:
"${diversifyInstruction}"

Please update ALL ${prompts.length} variant prompts by applying the user's requested changes while maintaining each variant's unique characteristics. Return exactly ${prompts.length} updated prompts in the same order.`;

  const result = await generateStructuredObject({
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ],
    schema,
    temperature: 0.7,
    maxTokens: 4000 // Higher token limit for multiple prompts
  });

  // Pad (with the originals) or trim to the expected count
  if (result.prompts.length !== prompts.length) {
    console.warn(`⚠️ LLM returned ${result.prompts.length} prompts but expected ${prompts.length}`);
    while (result.prompts.length < prompts.length) {
      result.prompts.push(prompts[result.prompts.length]);
    }
    result.prompts = result.prompts.slice(0, prompts.length);
  }

  return {
    success: true,
    prompts: result.prompts,
    notes: result.notes || null,
    timestamp: new Date().toISOString()
  };
}

// Prompt optimization system prompt for Stable Diffusion
const PROMPT_OPTIMIZATION_SYSTEM = `You are a prompt-engineering assistant specialized in Stable Diffusion image generation.

Your task:
Transform a short or poorly-specified user prompt into a highly detailed, high-quality Stable Diffusion prompt that maximizes visual clarity, aesthetic quality, and model interpretability — WITHOUT changing the user's original intent.

Rules:
1. Preserve the core subject, concept, and style requested by the user.
2. Do NOT introduce new objects, themes, or narrative elements that were not implied.
3. Expand the prompt with concrete visual details that Stable Diffusion understands well.
4. Favor descriptive visual language over abstract concepts.
5. Avoid prose — output a single, concise but richly detailed prompt.

Structure the output prompt using this order (omit sections if not applicable):
• Subject & composition
• Environment & background
• Lighting & mood
• Materials, textures, and surface detail
• Camera / framing / perspective
• Artistic style & medium
• Quality, detail, and rendering cues

Best practices:
- Use comma-separated descriptive clauses.
- Prefer specific visual terms over vague adjectives.
- Include lighting direction and mood when possible.
- Include camera terms only if relevant (e.g., portrait, macro, wide-angle).
- Avoid artist names unless explicitly requested.
- Avoid markdown, quotes, or commentary.
- Output ONLY the final enhanced prompt.`;

const optimizedPromptSchema = z.object({
  optimizedPrompt: z.string().describe('The enhanced Stable Diffusion prompt')
});

async function optimizePrompt(prompt) {
  const result = await generateStructuredObject({
    messages: [
      { role: 'system', content: PROMPT_OPTIMIZATION_SYSTEM },
      { role: 'user', content: `User prompt:\n${prompt}` }
    ],
    schema: optimizedPromptSchema,
    temperature: 0.7
  });

  return result.optimizedPrompt;
}

/**
 * Optimize prompts in parallel with a small concurrency limit. Each batch
 * settles fully before the next starts, and the first failure stops the run so
 * no further paid calls are made after an error.
 */
async function optimizeAllPrompts(combinations) {
  const CONCURRENCY_LIMIT = 4;
  const results = [];

  for (let i = 0; i < combinations.length; i += CONCURRENCY_LIMIT) {
    const batch = combinations.slice(i, i + CONCURRENCY_LIMIT);
    const settled = await Promise.allSettled(batch.map((combo) => optimizePrompt(combo.prompt)));

    const failure = settled.find((outcome) => outcome.status === 'rejected');
    if (failure) {
      throw failure.reason;
    }

    settled.forEach((outcome, index) => {
      results.push({
        ...batch[index],
        originalPrompt: batch[index].prompt,
        prompt: outcome.value,
        isOptimized: true
      });
    });
  }

  return results;
}

/**
 * Build a natural language prompt from wizard fields
 */
function buildPromptFromWizard(initialPrompt, fields) {
  let prompt = initialPrompt;

  // Group fields by pillar for natural ordering
  const pillarOrder = ['subject_action', 'environment_mood', 'composition_camera', 'style'];

  const sortedFields = [...fields].sort((a, b) => {
    const aIndex = pillarOrder.indexOf(a.pillar);
    const bIndex = pillarOrder.indexOf(b.pillar);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  const details = sortedFields
    .filter(f => f.values && f.values.length > 0)
    .map(f => f.values[0])
    .join(', ');

  if (details) {
    prompt += `. ${details}`;
  }

  return prompt;
}

/**
 * Generate optimized prompt combinations from wizard fields with multi-select answers.
 */
export async function generateWizardCombinations(initialPrompt, fields) {
  if (!initialPrompt) {
    throw new Error('Initial prompt is required');
  }

  if (!fields || !Array.isArray(fields)) {
    throw new Error('Fields array is required');
  }

  // Find faceted fields (multi-select)
  const facetedFields = fields.filter(f => f.isFaceted && f.values && f.values.length > 1);

  if (facetedFields.length === 0) {
    const basePrompt = buildPromptFromWizard(initialPrompt, fields);

    return {
      success: true,
      combinations: [{
        id: 'base',
        originalPrompt: basePrompt,
        prompt: await optimizePrompt(basePrompt),
        facetPath: [],
        description: 'Base prompt'
      }],
      stats: {
        totalCombinations: 1,
        facetCount: 0
      }
    };
  }

  // Generate all combinations
  const combinations = [];
  const variantIndices = facetedFields.map(() => 0);
  const maxIndices = facetedFields.map(f => f.values.length);

  let combinationIndex = 0;

  while (true) {
    // Build fields for this combination
    const modifiedFields = fields.map(field => {
      const facetedField = facetedFields.find(f => f.name === field.name);
      if (facetedField) {
        const facetIndex = facetedFields.indexOf(facetedField);
        const variantIndex = variantIndices[facetIndex];
        return {
          ...field,
          values: [facetedField.values[variantIndex]],
          isFaceted: false
        };
      }
      return field;
    });

    const prompt = buildPromptFromWizard(initialPrompt, modifiedFields);
    const facetPath = facetedFields.map((f, i) => ({
      fieldName: f.name,
      pillar: f.pillar,
      variantIndex: variantIndices[i],
      variant: f.values[variantIndices[i]]
    }));

    combinations.push({
      id: `combo-${combinationIndex}`,
      prompt,
      facetPath,
      description: facetPath.map(p => p.variant).join(' + ')
    });

    combinationIndex++;

    // Increment indices (like a counter with variable bases)
    let incrementIndex = 0;
    while (incrementIndex < variantIndices.length) {
      variantIndices[incrementIndex]++;
      if (variantIndices[incrementIndex] < maxIndices[incrementIndex]) {
        break;
      }
      variantIndices[incrementIndex] = 0;
      incrementIndex++;
    }

    // If we've wrapped all indices, we're done
    if (incrementIndex >= variantIndices.length) {
      break;
    }
  }

  const optimizedCombinations = await optimizeAllPrompts(combinations);

  return {
    success: true,
    combinations: optimizedCombinations,
    stats: {
      totalCombinations: optimizedCombinations.length,
      facetCount: facetedFields.length
    },
    timestamp: new Date().toISOString()
  };
}
